import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import AppBar, { AppBarProps } from '@mui/material/AppBar';
import Menu from '@mui/material/Menu';
import MenuIconMui from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import { io }                                                         from 'socket.io-client';
import { App }                                                       from '@capacitor/app';
import axios                                                         from 'axios';
import qs                                                            from 'qs';
import { FC, useContext, useMemo, useRef, useState }                 from 'react';
import { NavLink, useLocation, useNavigate }                          from 'react-router-dom';
import styled, { css }                                               from 'styled-components';
import { queryClient }                                               from '../App';
import { IFamily }                                                   from '../Auth/shared/interfaces';
import { MenuAnchor }                                                from '../Auth/shared/styles';
import { FamilyMemberCircle }                                        from '../modules/Dashboard/components/FamilyMembers/FamilyMemberCircle';
import { CHATS, CHAT_ROOMS, IChat, IMessage, useFetchChatById, useFetchChatsList } from '../modules/MessageCenter/queries';
import { LogoWhite }                                                 from '../shared/components/Logo';
import { Notistack }                                                 from '../shared/components/Notistack/Notistack';
import { EAppRoutes, EVENT_TYPE, PlatformNotificationType, familyCoreConfig }    from '../shared/constants/constants';
import { queryParams, withQueryParams }                              from '../shared/functions';
import { useFetchFamilyUser }                                        from '../shared/queries/family';
import { StyledBadge }                                               from '../shared/styledComponents';
import { generateInitials }                                          from '../shared/utils/commonFunctions';
import { logout }                                                    from '../shared/utils/setAuthorizationToken';
import AuthorizationContext                                          from '../shared/utils/withAuthorization/authorizationContext';
import { AddToFamilyCorePopover }                                    from './components/AddToFamilyCorePopover';
import { DynamicMessageCenter }                                      from './components/DynamicMessageCenter';
import { MessageNotification }                                       from './components/MessageNotification';
import { DRAWER_WIDTH }                                              from './Sidebar';
import { Notifications }                                             from './components/Notifications';
import { useChatHubConnection }                                      from './hooks/useChatHubConnection';
import { useNotificationsHubConnection }                             from './hooks/useNotificationsHubConnection';

interface IStyledHeaderProps extends AppBarProps {
  shifted: number;
}

const MenuIcon = styled(Menu)`&& {
  font-size: 30px;
  margin-right: 2px;
}`;

const Section = styled.div<{ key?: string }>`
  display: flex;
  align-items: center;
  min-width: 138px;
  justify-content: flex-end;
`;

const LogoSection = styled(Section)`
  ${ ({ theme: { breakpoints } }) => `${ breakpoints.up('lg') } { visibility: hidden; }` }
`;

const StyledHeader = styled(AppBar)<IStyledHeaderProps>`&& ${ ({ shifted, theme: { transitions, breakpoints } }) => css`{
  position         : absolute;
  background-color : #6f90d9;
  box-shadow       : none;

  ${ shifted && `width: calc(100% - ${ DRAWER_WIDTH }px)` };

  ${ breakpoints.up('lg') } {
    background-color: #ebf1f6;
  }

  transition: ${ transitions.create([ 'margin', 'width' ], {
    easing   : shifted ? transitions.easing.easeOut : transitions.easing.sharp,
    duration : shifted ? transitions.duration.enteringScreen : transitions.duration.leavingScreen,
  }) };
` }`;

const StyledLink = styled(NavLink)`&& {
  color: #262a32;
}`;

const StyledToolbar = styled(Toolbar)`&& {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: transparent;
  padding: 0 30px 0 10px;
}`;

const TransitionWrapper = styled.div<{ visible: boolean }>`
  position: relative;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${ StyledBadge } {
    transition: opacity .5s ease;
    opacity: ${ ({ visible }) => visible ? 1 : 0 }
  }

  .MuiCircularProgress-root {
    color: #fff;
    opacity: ${ ({ visible }) => visible ? 0 : 1 };
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    z-index: 1;
  }
`;

export const IconButtonSpaced = styled(IconButton)`&& {
  margin-right: -12px;
}`;

const HeaderMenuItem = styled(MenuItem)`&& {
  padding: 0 15px;
  font-size: 14px;
  line-height: 24px;

  &:first-child {
    padding-top: 4px;
  }

  &:last-child {
    padding-bottom: 4px;
  }
}`;

declare const window: any;

interface IHeaderOwnProps {
  family?: IFamily;
  isMobile: boolean;
  sidebarOpen: boolean;
  toggleDrawer: () => void;
}

export const notificationRedirection = (type: PlatformNotificationType, id?: string, userId?: string): string => {
  switch (type) {
    case(PlatformNotificationType.Checkin):
      return EAppRoutes.Locate;
    case(PlatformNotificationType.EventAdded):
    case(PlatformNotificationType.EventUpdated):
    case(PlatformNotificationType.EventDeleted):
    case(PlatformNotificationType.EventReminder):
      return `${EAppRoutes.Schedule}#key=${id}`;
    case(PlatformNotificationType.TaskReminder):
      return `${EAppRoutes.TasksSummary}/${id}`;
    case(PlatformNotificationType.TaskAdded):
      return `${EAppRoutes.TasksSummary}/${id}`;
    case(PlatformNotificationType.DocumentAdded):
      return `${EAppRoutes.DocumentsSummary}/${id}`;
    case(PlatformNotificationType.ContactAdded):
      return `${EAppRoutes.ContactsSummary}/${id}`;
    case(PlatformNotificationType.DocumentViewed):
      return `${EAppRoutes.DocumentsSummary}/${id}`;
    default:
      return EAppRoutes.Dashboard;
  }
};

export const setupSocketConnection = (hubPath: string, accessToken: string) => {
  const socket = io(`${axios.defaults.baseURL}${hubPath}`, {
    auth       : { token: accessToken },
    transports : ['websocket'],
  });

  return socket;
};

export const Header: FC<IHeaderOwnProps> = ({
  family,
  isMobile,
  sidebarOpen,
  toggleDrawer,
}) => {
  const { chatOpened, selectedChat } = queryParams();

  const menuAnchorRef = useRef(null);
  const currentUser   = useContext(AuthorizationContext);

  const [anchorEl, setAnchorEl]           = useState(null);
  const [isRefetchDone, setIsRefetchDone] = useState(false);

  const history    = useNavigate();
  const { search } = useLocation();

  const { data: user }                                                          = useFetchFamilyUser(family?.id);
  const { data: chat, isLoading: loadingChat }        = useFetchChatById(selectedChat);
  const { data: chatRooms, isLoading: loadingRooms, isFetching: fetchingRooms, dataUpdatedAt: chatRoomsUpdatedAt, refetch: refetchChatsList } = useFetchChatsList(user?.id);

  const setChatOpened = (chatOpened: boolean) => {
    history({
      search: qs.stringify({ ...queryParams(), chatOpened }, { skipNulls: true }),
    });
  };

  const onOpenChat = (roomId = '', messageId?: number) => {
    Notistack.closeSnackbar(messageId);

    history({
      search: qs.stringify({
        ...queryParams(),
        chatOpened   : !!roomId,
        selectedChat : roomId,
      }, { skipNulls: true }),
    });
  };

  const onSelectChat = (id: string) => {
    history({
      search: qs.stringify({
        ...queryParams(),
        chatOpened   : id ? true : null,
        selectedChat : id,
      }, { skipNulls: true }),
    });
  };

  // const roomExistsInCache = (roomId?: string): boolean => {
  //   const cachedChats: IChat[] = (queryClient.getQueryData([CHATS, user?.id]) as any)?.data?.data ?? [];
  //   return cachedChats.some(r => r.id === roomId);
  // };

  const updateChatHistory = (message: IMessage) => {
    //if (!roomExistsInCache(message?.roomId)) return;

    if (currentUser?.data?.id !== message?.user?.familyUserId) {
      const { selectedChat } = queryParams();
      const isCurrentRoom = message?.roomId === selectedChat;

      queryClient.setQueryData([CHAT_ROOMS, message?.roomId], (oldData: any) => {
        if (oldData?.data?.data?.messages) {
          return {
            ...oldData,
            data: {
              ...oldData?.data,
              data: {
                ...oldData?.data?.data,
                unreadMessagesCount : isCurrentRoom ? 0 : oldData.data.data.unreadMessagesCount + 1,
                messages            : [message, ...oldData?.data?.data?.messages],
              },
            },
          };
        }
        return oldData;
      });

      // Immediately update the chat list badge without waiting for the async refetch
      queryClient.setQueryData([CHATS, user?.id], (oldData: any) => {
        if (!oldData?.data?.data) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            data: oldData.data.data.map((room: IChat) =>
              room.id !== message?.roomId ? room : {
                ...room,
                unreadMessagesCount: isCurrentRoom ? 0 : room.unreadMessagesCount + 1,
              }
            ),
          },
        };
      });

      refetchChatsList();
    }
  };

  const onOpenNotification = (redirectRoute: string, key: number) => {
    history(redirectRoute);
    Notistack.closeSnackbar(key);
  };

  const createNotification = (data: any) => {
    const { EventType: type, Id: id } = data;

    const redirectRoute = notificationRedirection(type, id, user?.id);

    const Content = (key: number) => (
      <Notifications
        key={key}
        type={type}
        data={data}
        onOpen={() => onOpenNotification(redirectRoute, key)}
        onClose={() => Notistack.closeSnackbar(key)}
      />
    );

    {type && EVENT_TYPE[type] && (
      Notistack.enqueueSnackbar('', 'default', {
        preventDuplicate : true,
        autoHideDuration : 5000,
        action           : Content,
      })
    );
    };
  };

  const enrichMessageUser = (message: IMessage): IMessage => {
    if (message.user?.avatar) return message;

    const cachedChats: IChat[] = (queryClient.getQueryData([CHATS, user?.id]) as any)?.data?.data ?? [];
    const room = cachedChats.find(r => r.id === message.roomId);
    const fullUser = room?.users?.find(u =>
      u.id === message.user?.id || u.id === (message.user as any)?.familyUserId
    );

    return fullUser ? { ...message, user: { ...message.user, ...fullUser } } : message;
  };

  const createChatNotification = (message: IMessage) => {
    //if (!roomExistsInCache(message?.roomId)) return;

    const { chatOpened, selectedChat } = queryParams();
    const isOtherChatMessage = selectedChat && selectedChat !== message?.roomId;

    if ((isOtherChatMessage || !selectedChat || !chatOpened) && message.user) {
      //update chat history to ensure notifications are in sync with the latest messages, even if user doesn't click on the notification
      updateChatHistory(message);
      const enriched = enrichMessageUser(message);
      const Content = (key: number) => (
        <MessageNotification
          key={key}
          message={enriched}
          onOpen={() => onOpenChat(message?.roomId, key)}
          onClose={() => Notistack.closeSnackbar(key)}
        />
      );
      Notistack.enqueueSnackbar('', 'default', {
        preventDuplicate : false,
        autoHideDuration : 5000,
        action           : Content,
      });
    }
  };

  const createReceiveChatCreationNotification = (message: IMessage) => {
    // Show toast directly — avoids calling updateChatHistory which tries (and
    // fails silently) to patch a room that doesn't exist in the list yet, and
    // also fires its own competing refetchChatsList().
    const { chatOpened, selectedChat: currentSelected } = queryParams();
    const isOtherChatMessage = currentSelected && currentSelected !== message?.roomId;
    if ((isOtherChatMessage || !currentSelected || !chatOpened) && message.user) {
      const enriched = enrichMessageUser(message);
      const Content = (key: number) => (
        <MessageNotification
          key={key}
          message={enriched}
          onOpen={() => onOpenChat(message?.roomId, key)}
          onClose={() => Notistack.closeSnackbar(key)}
        />
      );
      Notistack.enqueueSnackbar('', 'default', {
        preventDuplicate : false,
        autoHideDuration : 5000,
        action           : Content,
      });
    }

    // Snapshot existing room IDs before refetch so we can detect the new room
    // even if message.roomId is missing from the socket payload.
    const existingRoomIds = new Set<string>(
      (queryClient.getQueryData([CHATS, user?.id]) as any)?.data?.data
        ?.map((r: IChat) => r.id) ?? []
    );

    refetchChatsList().then(() => {
      const { selectedChat } = queryParams();
      queryClient.setQueryData([CHATS, user?.id], (oldData: any) => {
        if (!oldData?.data?.data) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            data: oldData.data.data.map((room: IChat) => {
              const isSelected   = room.id === selectedChat;
              const isNewRoom    = !existingRoomIds.has(room.id);
              const isTargetRoom = message?.roomId ? room.id === message.roomId : isNewRoom;
              if (isTargetRoom && !isSelected && room.unreadMessagesCount === 0) {
                return { ...room, unreadMessagesCount: 1 };
              }
              return room;
            }),
          },
        };
      });
    });
  };

  useChatHubConnection({
    family,
    onReceiveMessage: updateChatHistory,
  });
  useNotificationsHubConnection({
    family,
    onReceiveNotification             : createNotification,
    onReceiveChatMessage              : createChatNotification,
    onReceiveChatCreationNotification : createReceiveChatCreationNotification,
  });

  const handleClick = () => {
    setAnchorEl(menuAnchorRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  App.addListener('appStateChange', ({ isActive }) => {
    if (isActive && !isRefetchDone) {
      refetchChatsList();
      setIsRefetchDone(true);
    } else if (!isActive) {
      setIsRefetchDone(false);
    }
  });

  return useMemo(() => (
    <StyledHeader shifted={+(sidebarOpen && !isMobile)}>
      <StyledToolbar
        disableGutters
        variant="dense"
      >
        <LogoSection>
          <IconButton
            color="inherit"
            onClick={toggleDrawer}
          >
              <MenuIcon as={MenuIconMui} />
          </IconButton>

          <NavLink to={`${ EAppRoutes.Dashboard }?id=${ user?.id }`}>
            <LogoWhite
              width="83px"
              height="34px"
            />
          </NavLink>
        </LogoSection>

        <Section>
          <AddToFamilyCorePopover
            familyId={family?.id}
            familyMemberId={user?.id}
          />

          { familyCoreConfig.isChatEnabled &&
              <DynamicMessageCenter
                chat={chat}
                user={user}
                loadingChat={loadingChat}
                loadingRooms={loadingRooms}
                fetchingRooms={fetchingRooms}
                chatRoomsUpdatedAt={chatRoomsUpdatedAt}
                chatRooms={chatRooms}
                setSelectedChat={onSelectChat}
                selectedChat={selectedChat}
                familyId={family?.id}
                chatOpened={search.includes('chatOpened=true')}
                setChatOpened={setChatOpened}
              />}

          <IconButtonSpaced
            color="inherit"
            onClick={handleClick}
          >
            <TransitionWrapper visible={!!currentUser}>
              <CircularProgress
                size={20}
                color="primary"
              />

              <StyledBadge
                color="secondary"
                badgeContent={0}
                aria-controls="user-menu"
                aria-haspopup="true"
              >
                <FamilyMemberCircle
                  fixed
                  fontSize="14px"
                  color={currentUser?.data?.color}
                  image={currentUser?.data?.avatar?.compactPath}
                  initials={generateInitials(currentUser?.data?.firstName, currentUser?.data?.lastName.trim())}
                />

                <MenuAnchor
                  ref={menuAnchorRef}
                  top="55px"
                  right="90px"
                />
              </StyledBadge>
            </TransitionWrapper>
          </IconButtonSpaced>

          <Menu
            keepMounted
            id="user-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <StyledLink to={EAppRoutes.MyProfile} onClick={handleClose}>
              <HeaderMenuItem>Settings</HeaderMenuItem>
            </StyledLink>

            <StyledLink to={withQueryParams(EAppRoutes.NotificationPreferences, { familyUserId: currentUser?.data?.id })} onClick={handleClose}>
              <HeaderMenuItem>Notifications</HeaderMenuItem>
            </StyledLink>

            <HeaderMenuItem onClick={logout}>Log Out</HeaderMenuItem>
          </Menu>
        </Section>
      </StyledToolbar>
    </StyledHeader>
  ), [
    chat,
    chatRooms,
    chatOpened,
    selectedChat,
    loadingChat,
    loadingRooms,
    fetchingRooms,
    chatRoomsUpdatedAt,
    anchorEl,
    currentUser,
    sidebarOpen,
    isMobile,
    window.location.search,
  ]);
};
